'use client';

import { useState, useEffect } from 'react';
import { ArrowRight, X } from 'lucide-react';
import type { Locale } from '@/lib/content';

const labels: Record<string, { text: string; cta: string }> = {
  nl: { text: 'Klaar om te beginnen? Test alle functies gratis.', cta: 'Gratis Account Aanmaken' },
  en: { text: 'Ready to start? Test all features for free.', cta: 'Create Free Account' },
  de: { text: 'Bereit zu starten? Testen Sie alle Funktionen kostenlos.', cta: 'Kostenloses Konto erstellen' },
  fr: { text: 'Pr\u00eat \u00e0 commencer ? Testez toutes les fonctionnalit\u00e9s gratuitement.', cta: 'Cr\u00e9er un compte gratuit' },
};

interface StickyCTABarProps {
  locale?: Locale;
}

export function StickyCTABar({ locale = 'nl' }: StickyCTABarProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('sticky-cta-dismissed')) {
      setDismissed(true);
      return;
    }
    const handleScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (dismissed || !visible) return null;

  const l = labels[locale] || labels.nl;

  return (
    <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-40 shadow-lg animate-fade-in-up">
      <p className="text-sm font-semibold text-navy hidden sm:block">{l.text}</p>
      <div className="flex items-center gap-3 w-full sm:w-auto">
        <a
          href={`/${locale}/signup/`}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-cta text-ink px-5 py-2.5 rounded-lg font-semibold text-sm hover:scale-[1.03] transition-transform"
          data-track="Sticky CTA Bar"
          data-track-category="CTA"
        >
          {l.cta}
          <ArrowRight className="h-4 w-4" />
        </a>
        <button
          onClick={() => {
            setDismissed(true);
            sessionStorage.setItem('sticky-cta-dismissed', '1');
          }}
          className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Sluiten"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
